import { useState, useEffect } from 'react';
import { IoSearchOutline, IoClose, IoBusinessOutline, IoCheckmarkCircleOutline, IoTimeOutline } from 'react-icons/io5';
import { useAuth } from '../../contexts/AuthContext';
import { churchApi } from '../../services/api';
import './OnboardingStep2Church.css';

const OnboardingStep2Church = ({ onNext, onSkip, isLoading }) => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [churches, setChurches] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedChurch, setSelectedChurch] = useState(null);
  const [joinCode, setJoinCode] = useState('');
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState('');
  const [membershipStatus, setMembershipStatus] = useState(null);

  // Load user's current church if they have one
  useEffect(() => {
    if (user?.church) {
      setSelectedChurch(user.church);
      setMembershipStatus(user.churchMembershipStatus || 'approved');
    }
  }, [user]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setChurches([]);
      return;
    }

    setSearching(true);
    setError('');

    try {
      const response = await churchApi.searchChurches(searchQuery);
      setChurches(response.data.churches || []);
    } catch (error) {
      console.error('Error searching churches:', error);
      setError('Failed to search churches. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const handleJoinChurch = async (church) => {
    setJoining(true);
    setError('');

    try {
      const response = await churchApi.joinChurch(church.id);
      setSelectedChurch(response.data.user.church);
      setMembershipStatus(response.data.membershipStatus);
      setChurches([]);
      setSearchQuery('');
    } catch (error) {
      console.error('Error joining church:', error);
      setError(error.response?.data?.message || 'Failed to join church. Please try again.');
    } finally {
      setJoining(false);
    }
  };

  const handleJoinByCode = async () => {
    if (!joinCode.trim()) {
      setError('Please enter a join code');
      return;
    }

    setJoining(true);
    setError('');

    try {
      const response = await churchApi.joinByCode(joinCode);
      setSelectedChurch(response.data.user.church);
      setMembershipStatus(response.data.membershipStatus);
      setJoinCode('');
    } catch (error) {
      console.error('Error joining church by code:', error);
      setError(error.response?.data?.message || 'Invalid join code. Please try again.');
    } finally {
      setJoining(false);
    }
  };

  const handleLeaveChurch = async () => {
    setJoining(true);
    setError('');

    try {
      await churchApi.leaveChurch();
      setSelectedChurch(null);
      setMembershipStatus(null);
    } catch (error) {
      console.error('Error leaving church:', error);
      setError('Failed to leave church. Please try again.');
    } finally {
      setJoining(false);
    }
  };

  const handleContinue = () => {
    onNext();
  };

  const handleSkip = () => {
    onSkip();
  };

  const getStatusIcon = () => {
    switch (membershipStatus) {
      case 'approved':
        return <IoCheckmarkCircleOutline className="status-icon approved" />;
      case 'pending':
        return <IoTimeOutline className="status-icon pending" />;
      default:
        return null;
    }
  };

  const getStatusText = () => {
    switch (membershipStatus) {
      case 'approved':
        return 'Member';
      case 'pending':
        return 'Pending Approval';
      default:
        return '';
    }
  };

  return (
    <div className="onboarding-church-step">
      <div className="church-step-content">
        <div className="church-intro">
          <IoBusinessOutline className="church-icon" />
          <h3>Join Your Church</h3>
          <p>
            Connect with your church community to participate in reading challenges together. 
            You can search for your church to join (pending admin approval) or use a join code to join immediately.
          </p>
        </div>

        {/* Current Church Display */}
        {selectedChurch && (
          <div className="current-church">
            <h4>Your Church</h4>
            <div className="church-card selected">
              <div className="church-info">
                <h5>{selectedChurch.name}</h5>
                {selectedChurch.description && (
                  <p className="church-description">{selectedChurch.description}</p>
                )}
                <div className="membership-status">
                  {getStatusIcon()}
                  <span className={`status-text ${membershipStatus}`}>
                    {getStatusText()}
                  </span>
                </div>
              </div>
              <button 
                className="btn-leave-church"
                onClick={handleLeaveChurch}
                disabled={joining}
              >
                Leave Church
              </button>
            </div>
            
            {membershipStatus === 'pending' && (
              <div className="pending-notice">
                <IoTimeOutline size={16} />
                <span>Your request is pending approval from the church administrator.</span>
              </div>
            )}
          </div>
        )}

        {/* Church Search */}
        {!selectedChurch && (
          <div className="church-search-section">
            <h4>Find Your Church</h4>
            
            <div className="search-container">
              <div className="search-input-group">
                <input
                  type="text"
                  placeholder="Search for your church..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  disabled={searching}
                />
                <button 
                  className="btn-search"
                  onClick={handleSearch}
                  disabled={searching || !searchQuery.trim()}
                >
                  <IoSearchOutline size={20} />
                </button>
              </div>
            </div>

            {/* Search Results */}
            {churches.length > 0 && (
              <div className="search-results">
                <h5>Search Results</h5>
                {churches.map(church => (
                  <div key={church.id} className="church-card">
                    <div className="church-info">
                      <h6>{church.name}</h6>
                      {church.description && (
                        <p className="church-description">{church.description}</p>
                      )}
                      {church.admin && (
                        <p className="church-admin">Admin: {church.admin.name}</p>
                      )}
                    </div>
                    <button 
                      className="btn-join-church"
                      onClick={() => handleJoinChurch(church)}
                      disabled={joining}
                    >
                      Join (Pending Approval)
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Join by Code */}
            <div className="join-by-code-section">
              <h4>Join with Code (Skip Approval)</h4>
              <p>Have a join code from your church administrator? You'll be approved immediately.</p>
              
              <div className="code-input-group">
                <input
                  type="text"
                  placeholder="Enter join code..."
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                  onKeyPress={(e) => e.key === 'Enter' && handleJoinByCode()}
                  disabled={joining}
                  maxLength={20}
                />
                <button 
                  className="btn-join-by-code"
                  onClick={handleJoinByCode}
                  disabled={joining || !joinCode.trim()}
                >
                  Join Now
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="error-message">
            <IoClose size={16} />
            <span>{error}</span>
          </div>
        )}

        {/* Action Buttons */}
        <div className="church-step-actions">
          <button 
            className="btn-skip"
            onClick={handleSkip}
            disabled={isLoading || joining}
          >
            Skip for Now
          </button>
          
          <button 
            className="btn-continue"
            onClick={handleContinue}
            disabled={isLoading || joining}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingStep2Church;
